import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Flame,
  Footprints,
  Lightbulb,
  Loader2,
  Lock,
  MessageCircle,
  Quote,
  Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { MiniTrend } from "@/components/recovery/mini-trend";
import { STATUS_SHAPES, type StatusKey } from "@/lib/status";
import {
  ApiError,
  getSponseeDetail,
  sendSponseeNote,
  type SponseeDetailData,
} from "@/lib/api";
import { entryWhen } from "@/lib/format";

const STATUS_LABEL: Record<StatusKey, string> = {
  green: "On Track",
  yellow: "Caution",
  red: "Needs Attention",
};
const BAR_TINT: Record<StatusKey, string> = {
  green: "bg-status-green",
  yellow: "bg-status-yellow",
  red: "bg-status-red",
};
const BAR_H: Record<StatusKey, string> = { green: "80%", yellow: "55%", red: "34%" };

function StatusBadge({ status }: { status: StatusKey }) {
  const Shape = STATUS_SHAPES[status];
  return (
    <Badge status={status}>
      <Shape className="h-[11px] w-[11px]" strokeWidth={2} />
      {STATUS_LABEL[status]}
    </Badge>
  );
}
function firstName(n: string) {
  return n.split(" ")[0] || n;
}

export default function SponseeDetail() {
  const navigate = useNavigate();
  const { ownerId = "" } = useParams();
  const [data, setData] = useState<SponseeDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    getSponseeDetail(ownerId)
      .then(setData)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) navigate("/signin");
        else if (e instanceof ApiError && e.status === 403) setError("This isn't shared with you.");
        else setError("Couldn't load this sponsee.");
      })
      .finally(() => setLoading(false));
  }, [ownerId, navigate]);

  async function send() {
    if (!msg.trim()) return;
    setSending(true);
    try {
      await sendSponseeNote(ownerId, msg.trim());
      setSent(true);
    } catch {
      setError("Couldn't send your note.");
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-sm text-muted-foreground">{error ?? "Not available."}</p>
        <Button variant="outline" onClick={() => navigate("/sponsor")}>
          Back to Sponsees
        </Button>
      </div>
    );
  }

  const name = firstName(data.name);
  const full = data.share_level === "full_history";

  return (
    <div className="pb-16">
      <div className="flex items-center gap-2 px-4 pb-2 pt-6">
        <Button variant="ghost" size="icon" aria-label="Back" onClick={() => navigate("/sponsor")}>
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </Button>
        <h1 className="flex-1 text-lg font-semibold text-foreground">{data.name}</h1>
      </div>

      <div className="flex flex-col gap-3 px-6 pt-2">
        <Card className="bg-secondary">
          <CardContent className="flex items-start gap-2.5 px-4 py-3">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
            <p className="text-[12.5px] leading-relaxed text-muted-foreground">
              You can see {name}'s status, streak, and sobriety time.{" "}
              {full ? "Notes appear on the entries they shared." : "Notes stay private at this share level."}
            </p>
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 gap-3">
          <Card>
            <CardContent className="px-4 py-3.5">
              <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">Streak</p>
              <p className="mt-1.5 flex items-center gap-1.5 text-xl font-bold text-foreground">
                <Flame className="h-[17px] w-[17px] text-status-yellow" strokeWidth={2} />
                {data.streak} {data.streak === 1 ? "day" : "days"}
              </p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {data.checked_in_today ? "Checked in today" : "Not yet today"}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="px-4 py-3.5">
              <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">Sobriety</p>
              <p className="mt-1.5 flex items-center gap-1.5 text-xl font-bold text-foreground">
                <Shield className="h-[17px] w-[17px] text-primary" strokeWidth={2} />
                {data.sobriety_start_date ? sinceDays(data.sobriety_start_date) : "—"}
              </p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {data.sobriety_start_date
                  ? data.terminology.neutral
                    ? "days"
                    : `days ${data.terminology.adj}`
                  : "not set"}
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">This Week</p>
            <div className="mt-3.5 flex h-24 items-end gap-2">
              {data.week.map((d) => (
                <div key={d.date} className="flex h-full flex-1 flex-col items-center justify-end gap-1.5">
                  <div
                    className={`w-full rounded ${d.status ? BAR_TINT[d.status] : "bg-muted"}`}
                    style={{ height: d.status ? BAR_H[d.status] : "8%" }}
                  />
                  <span className="text-[11px] text-muted-foreground">{weekdayLetter(d.date)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="px-4 py-3.5">
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">Last 14 Days</p>
            <MiniTrend data={data.strip} className="mt-2.5" barClassName="h-6" />
          </CardContent>
        </Card>

        <p className="mt-1 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
          Shared Check-Ins
        </p>
        {data.entries.length === 0 && (
          <p className="text-sm text-muted-foreground">No check-ins shared yet.</p>
        )}
        {data.entries.map((e) => (
          <Card
            key={e.id}
            className={e.overall_status === "red" ? "border-2 border-status-red" : ""}
          >
            <CardContent className="px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{entryWhen(e.created_at)}</p>
                  <p className="text-xs text-muted-foreground">
                    {e.item_count > 0 ? `${e.item_count} question${e.item_count === 1 ? "" : "s"}` : "Mood only"}
                  </p>
                </div>
                {e.overall_status && <StatusBadge status={e.overall_status} />}
              </div>
              {e.note ? (
                <div className="mt-2.5 flex gap-2 border-t border-border pt-2.5">
                  <Quote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" strokeWidth={2} />
                  <p className="text-[13px] leading-relaxed text-foreground">{e.note}</p>
                </div>
              ) : e.note_private ? (
                <div className="mt-2 flex items-center gap-1.5">
                  <Lock className="h-3 w-3 text-muted-foreground" strokeWidth={2} />
                  <span className="text-xs text-muted-foreground">Note kept private</span>
                </div>
              ) : null}
            </CardContent>
          </Card>
        ))}

        {data.ideas.length > 0 && (
          <>
            <p className="mt-1 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Shared Bright Ideas
            </p>
            {data.ideas.map((it, i) => (
              <Card key={i}>
                <CardContent className="flex items-start gap-2.5 px-4 py-3">
                  <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
                  <p className="text-sm text-foreground">{it.content}</p>
                </CardContent>
              </Card>
            ))}
          </>
        )}

        {data.steps.length > 0 && (
          <>
            <p className="mt-1 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Step Work
            </p>
            {data.steps.map((s) => (
              <Card
                key={s.step_number}
                interactive
                onClick={() => navigate(`/sponsor/${ownerId}/steps/${s.step_number}`)}
              >
                <CardContent className="flex items-center gap-3 px-4 py-3.5">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Footprints className="h-5 w-5" strokeWidth={2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">
                      {s.name}
                      {s.principle ? ` — ${s.principle}` : ""}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {s.step_number === 5
                        ? s.status === "worked"
                          ? "Read to you in person · nothing recorded"
                          : "Happens in person — they mark it after"
                        : s.step_number === 4
                          ? "Written privately · never shared through the app"
                          : s.is_shared === 1
                            ? "Reflections shared with you"
                            : "Ready to talk · writing kept private"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge status={s.status === "worked" ? "green" : "yellow"}>
                      {s.status === "worked"
                        ? "Worked"
                        : s.status === "awaiting"
                          ? "In person"
                          : s.status === "in_progress"
                            ? "In progress"
                            : "Ready"}
                    </Badge>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={2} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        )}

        <p className="mt-2 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">Reach Out</p>
        {sent ? (
          <Card className="border-primary">
            <CardContent className="flex items-start gap-2.5 px-4 py-3.5">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
              <div>
                <p className="text-sm font-medium text-foreground">Sent to {name}</p>
                <p className="mt-0.5 text-[13px] text-muted-foreground">
                  It's on its way to their inbox. You're one call away.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="flex flex-col gap-2.5 px-4 py-3.5">
              <Textarea
                rows={3}
                placeholder={`A short note to ${name}`}
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
              />
              <Button disabled={sending || !msg.trim()} onClick={send}>
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                ) : (
                  <>
                    <MessageCircle className="h-4 w-4" strokeWidth={2} />
                    Send a note
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}
        <p className="text-center text-xs text-muted-foreground">
          Encouragement, not evaluation. One day at a time.
        </p>
      </div>
    </div>
  );
}

function sinceDays(dateISO: string): number {
  const start = new Date(`${dateISO}T00:00:00`);
  return Math.max(0, Math.floor((Date.now() - start.getTime()) / 86_400_000));
}
function weekdayLetter(dateISO: string): string {
  const [y, m, d] = dateISO.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short" }).charAt(0);
}
